// src/components/domains/advisor-portal/dashboard/DailySummary.js

import React from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useTheme } from "../../../../contexts/ThemeContext";
import DashboardCard from "../../../shared/DashboardCard";
import { SunIcon } from "@heroicons/react/24/outline";

const DailySummary = ({
  meetings,
  tasks,
  marketTrend,
  marketReason = "market conditions",
  advisorFirstName,
}) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  const handleNavigate = (path) => {
    navigate(path);
  };

  const BulletPoint = () => (
    <div
      className={`
        w-1.5 h-1.5 rounded-full 
        ${isDarkMode ? "bg-teal-500" : "bg-[#029831]"} 
        mt-1.5 flex-shrink-0
      `}
    />
  );

  const getOrdinalSuffix = (d) => {
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const getTimeOfDayGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const today = new Date();
  const dayOfMonth = format(today, "d");
  const ordinalSuffix = getOrdinalSuffix(parseInt(dayOfMonth, 10));
  const formattedDate = format(today, `EEEE, MMMM d'${ordinalSuffix}', yyyy`);

  return (
    <DashboardCard
      title="Daily Briefing"
      icon={SunIcon}
      headerControls={
        <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
          {formattedDate}
        </div>
      }
    >
      <div className="px-3 py-2">
        <div className="space-y-2">
          {/* Greeting */}
          <div className="flex items-start gap-1.5 mb-2">
            <h1 className={`
              text-sm font-semibold leading-tight
              ${isDarkMode ? "text-gray-100" : "text-gray-900"}
            `}>
              {`${getTimeOfDayGreeting()}, ${advisorFirstName}!`}
            </h1>
          </div>

          {/* 1) You have X meetings today */}
          <div className="flex items-start gap-1.5 group">
            <BulletPoint />
            <span className={`text-[11px] leading-snug ${isDarkMode ? "text-gray-300" : "text-gray-600 dark:text-gray-400"}`}>
              You have{" "}
              <button
                onClick={() => handleNavigate("/advisor-portal/calendar-events")}
                className={`font-medium ${isDarkMode ? "text-teal-400 hover:text-teal-500" : "text-[#029831] hover:text-[#018529]"} group-hover:underline`}
              >
                {meetings} meetings
              </button>{" "}
              today
            </span>
          </div>

          {/* 2) You have Y follow up tasks to complete */}
          <div className="flex items-start gap-1.5 group">
            <BulletPoint />
            <span className={`text-[11px] leading-snug ${isDarkMode ? "text-gray-300" : "text-gray-600 dark:text-gray-400"}`}>
              You have{" "}
              <button
                onClick={() => handleNavigate("/advisor-portal/tasks")}
                className={`font-medium ${isDarkMode ? "text-teal-400 hover:text-teal-500" : "text-[#029831] hover:text-[#018529]"} group-hover:underline`}
              >
                {tasks} follow up tasks
              </button>{" "}
              to complete
            </span>
          </div>

          {/* 3) If you're feeling ambitious... */}
          <div className="flex items-start gap-1.5 group">
            <BulletPoint />
            <span className={`text-[11px] leading-snug ${isDarkMode ? "text-gray-300" : "text-gray-600 dark:text-gray-400"}`}>
              If you're feeling ambitious you can consider these additional tasks today
            </span>
          </div>

          {/* 4) The stock market is trending up/down... */}
          <div className="flex items-start gap-1.5 group">
            <BulletPoint />
            <span className={`text-[11px] leading-snug ${isDarkMode ? "text-gray-300" : "text-gray-600 dark:text-gray-400"}`}>
              The stock market is trending{" "}
              <button
                onClick={() => handleNavigate("/portfolio-management/market-news")}
                className={`font-medium group-hover:underline ${
                  marketTrend === "up"
                    ? isDarkMode
                      ? "text-emerald-400 hover:text-emerald-300"
                      : "text-emerald-600 hover:text-emerald-700"
                    : isDarkMode
                      ? "text-rose-400 hover:text-rose-300"
                      : "text-red-600 hover:text-red-700"
                }`}
              >
                {marketTrend}
              </button>{" "}
              today due to {marketReason}
            </span>
          </div>
        </div>
      </div>
    </DashboardCard>
  );
};

export default DailySummary;
